import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Button, cn } from '@taki/ui';
import type { PersonneVue } from '@taki/ui';
import { dateDuCalendrier, dateDuDelai, libelleDeLaTache, suiteDe } from './suivi.js';
import { jourPur, libelleDuJour } from './temps.js';

/** Le choix « Autre date » : le calendrier du navigateur, celui des champs date du socle. */
const CALENDRIER = 'calendrier';

const CLASSES_DU_CHAMP =
  'rounded-sm border border-border bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-300 ' +
  'focus-visible:border-accent-border focus-visible:outline-none';

/** Ce que le cadre rend au dossier pour creer la tache. */
export interface TacheDuSuivi {
  libelle: string;
  date: Date;
  assigne: string;
  /** Vrai : la date se pose aussi en rappel sur les vaccins de la consultation. */
  avecVaccins: boolean;
}

/**
 * LA SUITE D'UN SUIVI, sous le choix « Suivi » du compte rendu : trois delais
 * ou « Autre date » et son calendrier, pour qui, puis « Creer la tache ». Avec
 * « Autre », le libelle s'ecrit a la main (recette du fondateur le 26/10 : une
 * tache libre passe par le meme cadre, pas par un formulaire a part). Tous les
 * choix sont des pastilles, comme le reste du compte rendu. L'onglet Taches
 * reutilise ce cadre avec `suivi="Autre"`.
 *
 * La creation est au dossier (`onCreer`), qui tient le SDK et recharge les
 * taches.
 */
export function PanneauSuivi({
  suivi,
  depuis,
  animal,
  vaccins,
  personnes,
  assigneParDefaut,
  onCreer,
}: {
  suivi: unknown;
  /** Le jour de la consultation : les delais se comptent de la. */
  depuis: Date;
  /** Le nom de l'animal, dans le libelle des taches proposees. */
  animal: string;
  /** Le nombre de lignes de vaccin de la consultation (rappel de vaccin seulement). */
  vaccins: number;
  /** L'annuaire de l'org : les pastilles « Pour ». */
  personnes: readonly PersonneVue[];
  assigneParDefaut: string;
  onCreer: (tache: TacheDuSuivi) => Promise<boolean>;
}): JSX.Element | null {
  const suite = suiteDe(suivi);
  const [choisi, setChoisi] = useState<string | null>(suite?.parDefaut ?? null);
  const [avecVaccins, setAvecVaccins] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [jourChoisi, setJourChoisi] = useState('');
  const [texte, setTexte] = useState('');
  const [assigne, setAssigne] = useState(assigneParDefaut);
  useEffect(() => setChoisi(suite?.parDefaut ?? null), [suite]);
  useEffect(() => setAssigne(assigneParDefaut), [assigneParDefaut]);

  if (suite === null) return null;

  const delai = suite.delais.find((d) => d.libelle === choisi) ?? null;
  const date = choisi === CALENDRIER ? dateDuCalendrier(jourChoisi) : delai === null ? null : dateDuDelai(depuis, delai);
  const libelle = suite.libre === true ? texte.trim() : libelleDeLaTache(suite, animal);
  const pret = date !== null && libelle !== '';
  const equipe = personnes.filter((p) => p.statut === 'actif' || p.personId === assigne);
  const nomDe = (personId: string): string => {
    const p = personnes.find((x) => x.personId === personId);
    return p === undefined ? '' : p.displayName || p.email;
  };
  const creer = async (): Promise<void> => {
    if (!pret || date === null || enCours) return;
    setEnCours(true);
    const fait = await onCreer({ libelle, date, assigne, avecVaccins: suite.rappelDesVaccins === true && vaccins > 0 && avecVaccins });
    setEnCours(false);
    if (fait) setTexte('');
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 px-4 py-3">
      {suite.libre === true ? (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-500">Tâche</span>
          <input
            className={cn(CLASSES_DU_CHAMP, 'h-9')}
            value={texte}
            placeholder="Appeler le propriétaire pour les résultats"
            onChange={(e) => setTexte(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void creer();
            }}
          />
        </label>
      ) : null}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-500">Quand</span>
        <div className="flex flex-wrap gap-2">
          {suite.delais.map((d) => (
            <Button key={d.libelle} size="sm" variant={d.libelle === choisi ? 'dark' : 'secondary'} onClick={() => setChoisi(d.libelle)}>
              {d.libelle}
            </Button>
          ))}
          <Button
            size="sm"
            variant={choisi === CALENDRIER ? 'dark' : 'secondary'}
            onClick={() => {
              setChoisi(CALENDRIER);
              if (jourChoisi === '') setJourChoisi(jourPur(delai === null ? depuis : dateDuDelai(depuis, delai)));
            }}
          >
            Autre date
          </Button>
          {choisi === CALENDRIER ? (
            <input
              type="date"
              aria-label="Date du suivi"
              className={cn(CLASSES_DU_CHAMP, 'h-8')}
              value={jourChoisi}
              min={jourPur(depuis)}
              onChange={(e) => setJourChoisi(e.target.value)}
            />
          ) : null}
        </div>
      </div>
      {equipe.length === 0 ? null : (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-500">Pour</span>
          <div className="flex flex-wrap gap-2">
            {equipe.map((p) => (
              <Button key={p.personId} size="sm" variant={p.personId === assigne ? 'dark' : 'secondary'} onClick={() => setAssigne(p.personId)}>
                {p.displayName || p.email}
              </Button>
            ))}
          </div>
        </div>
      )}
      {date === null ? null : (
        <p className="text-sm text-ink-900">
          {libelle === '' ? 'Une tâche' : `« ${libelle} »`} le {libelleDuJour(date).toLowerCase()} {date.getFullYear()}
          {nomDe(assigne) === '' ? '' : `, pour ${nomDe(assigne)}`}.
        </p>
      )}
      {suite.rappelDesVaccins === true && vaccins > 0 ? (
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={avecVaccins} onChange={(e) => setAvecVaccins(e.target.checked)} className="h-4 w-4 accent-ink-900" />
          Poser aussi cette date de rappel sur {vaccins > 1 ? `les ${vaccins} vaccins` : 'le vaccin'} de la consultation
        </label>
      ) : null}
      <div>
        <Button size="sm" disabled={!pret || enCours} onClick={() => void creer()}>
          Créer la tâche
        </Button>
      </div>
    </div>
  );
}
